import type { InjectedData } from "../../layout/types.js";
import { mergePatches } from "./helpers.js";
import { createBehaviorNode } from "./BehaviorFactory.js";
import { BehaviorNode } from "./nodes/BehaviorNode.js";
import { EventBehaviorNode } from "./nodes/EventBehaviorNode.js";
import {
  BehaviorEdge,
  BehaviorFlow,
  CurrentNodeContext,
  CustomEventRuntimeEntry,
  FunctionRuntimeEntry,
  VariableRuntimeEntry,
  VariableOverrideApi,
  NodeExecutionResult,
  PatchResult,
  RuntimeResult,
  ExecutionContext,
  ScheduledNode,
  RuntimeSignal,
} from "./types.js";
import { BehaviorGraphPayload } from "./types.js";
import { FUNCTION_FLOW_OUT_PORT_ID } from "../function-graph.js";

export class BehaviorRuntime {
  private runCounter = 0;

  constructor(public readonly nodes: Map<string, BehaviorNode>) {}

  static build(graph: BehaviorGraphPayload): BehaviorRuntime {
    const { nodes: behaviorNodes, connections } = graph;
    const nodes = new Map<string, BehaviorNode>();

    for (const behavior of behaviorNodes) {
      nodes.set(behavior.id, createBehaviorNode(behavior));
    }

    const runtime = new BehaviorRuntime(nodes);

    for (const conn of connections) {
      const fromNode = nodes.get(conn.fromNodeId);
      const toNode = nodes.get(conn.toNodeId);
      if (!fromNode || !toNode) continue;
      const edge: BehaviorEdge = {
        fromNode,
        fromPort: conn.fromPortId,
        node: toNode,
        port: conn.toPortId,
      };

      const outgoing = fromNode.outgoingEdgesByPort.get(conn.fromPortId) ?? [];
      outgoing.push(edge);
      fromNode.outgoingEdgesByPort.set(conn.fromPortId, outgoing);

      const incoming = toNode.incomingEdgesByPort.get(conn.toPortId) ?? [];
      incoming.push(edge);
      toNode.incomingEdgesByPort.set(conn.toPortId, incoming);
    }

    return runtime;
  }

  createContext(
    nowMs: number,
    data: InjectedData,
    previousData: InjectedData | undefined,
    currentNodeContext?: CurrentNodeContext,
    sceneAnimationsByName?: Record<
      string,
      import("./types.js").BehaviorAnimationPayload
    >,
    controlRoomSignals?: string[],
    customEventRuntimeEntries?: ReadonlyMap<string, CustomEventRuntimeEntry>,
    functionRuntimeEntries?: ReadonlyMap<string, FunctionRuntimeEntry>,
    variableRuntimeEntries?: ReadonlyMap<string, VariableRuntimeEntry>,
    variableOverrideApi?: VariableOverrideApi,
    customEventEvalCache?: Map<string, boolean>,
  ): ExecutionContext {
    this.runCounter += 1;
    return {
      id: `run-${this.runCounter}`,
      nowMs,
      loopStack: [],
      data,
      currentNodeContext,
      previousData,
      sceneAnimationsByName,
      listTransitionSignal: data.listTransitionSignal,
      controlRoomSignals,
      customEventRuntimeEntries,
      customEventEvalCache,
      functionRuntimeEntries,
      variableRuntimeEntries,
      variableOverrideApi,
      parentRuntime: this,
    };
  }

  /**
   * Seeds flow edges from a `functionBegin` output port (defaults to main `out`).
   */
  buildFlowsFromFunctionPort(
    context: ExecutionContext,
    portId: string = FUNCTION_FLOW_OUT_PORT_ID,
  ): BehaviorFlow[] {
    const beginNode = [...this.nodes.values()].find(
      (node) =>
        node.behavior.type === "control" &&
        node.behavior.controlType === "functionBegin",
    );
    if (!beginNode) return [];

    return (beginNode.outgoingEdgesByPort.get(portId) ?? []).map((edge) => ({
      edge,
      context,
    }));
  }

  /**
   * Seeds flow edges from the single `functionBegin` node's main `out` port.
   */
  buildFlowsFromFunctionBegin(context: ExecutionContext): BehaviorFlow[] {
    return this.buildFlowsFromFunctionPort(context, FUNCTION_FLOW_OUT_PORT_ID);
  }

  getNode(nodeId: string): BehaviorNode | undefined {
    return this.nodes.get(nodeId);
  }

  getEventNodes(): EventBehaviorNode[] {
    return [...this.nodes.values()].filter(
      (node): node is EventBehaviorNode => node instanceof EventBehaviorNode,
    );
  }

  getDueScheduledNodes(
    nowMs: number,
    scheduledNodes: ScheduledNode[],
  ): ScheduledNode[] {
    return scheduledNodes.filter((node) => node.at <= nowMs);
  }

  getNextWakeAt(scheduledNodes: ScheduledNode[]): number | undefined {
    if (scheduledNodes.length === 0) return undefined;
    return scheduledNodes.reduce(
      (min, current) => Math.min(min, current.at),
      scheduledNodes[0]!.at,
    );
  }

  filterExpiredScheduledNodes(
    nowMs: number,
    scheduledNodes: ScheduledNode[],
  ): ScheduledNode[] {
    return scheduledNodes.filter((node) => node.at > nowMs);
  }

  seedFlowsFromEvents(
    nowMs: number,
    data: InjectedData,
    previousData: InjectedData | undefined,
    scheduledNodes: ScheduledNode[],
    currentNodeContext?: CurrentNodeContext,
    sceneAnimationsByName?: Record<
      string,
      import("./types.js").BehaviorAnimationPayload
    >,
    controlRoomSignals?: string[],
    customEventRuntimeEntries?: ReadonlyMap<string, CustomEventRuntimeEntry>,
    functionRuntimeEntries?: ReadonlyMap<string, FunctionRuntimeEntry>,
    variableRuntimeEntries?: ReadonlyMap<string, VariableRuntimeEntry>,
    variableOverrideApi?: VariableOverrideApi,
  ): BehaviorFlow[] {
    const flows: BehaviorFlow[] = [];
    const customEventEvalCache = new Map<string, boolean>();
    const context = this.createContext(
      nowMs,
      data,
      previousData,
      currentNodeContext,
      sceneAnimationsByName,
      controlRoomSignals,
      customEventRuntimeEntries,
      functionRuntimeEntries,
      variableRuntimeEntries,
      variableOverrideApi,
      customEventEvalCache,
    );

    for (const eventNode of this.getEventNodes()) {
      if (!eventNode.shouldFire(context)) continue;

      const result = eventNode.execute(context);
      flows.push(...result.nextFlows);

      if (result.scheduledNodes.length > 0) {
        scheduledNodes.push(...result.scheduledNodes);
      }
    }

    return flows;
  }

  private createLoopContinuationFlow(
    context: ExecutionContext,
  ): BehaviorFlow[] {
    const loop = context.loopStack[context.loopStack.length - 1];
    if (!loop) return [];

    const repeatNode = this.getNode(loop.nodeId);
    if (!repeatNode) return [];

    return [
      {
        edge: {
          fromNode: repeatNode,
          fromPort: "__loop_continue__",
          node: repeatNode,
          port: "__loop_continue__",
        },
        context,
      },
    ];
  }

  private handleRuntimeSignal(signal: RuntimeSignal): BehaviorFlow[] {
    switch (signal.type) {
      case "loop_continue":
        return this.createLoopContinuationFlow(signal.context);

      case "loop_done":
      case "event_output_reached":
      case "function_end_reached":
        return [];

      default:
        return [];
    }
  }

  seedFlowsFromDueScheduled(
    nowMs: number,
    scheduledNodes: ScheduledNode[],
  ): {
    flows: BehaviorFlow[];
    runtimeSignals: RuntimeSignal[];
    scheduledNodes: ScheduledNode[];
  } {
    const due = this.getDueScheduledNodes(nowMs, scheduledNodes);
    const remainingScheduledNodes = this.filterExpiredScheduledNodes(
      nowMs,
      scheduledNodes,
    );

    const flows: BehaviorFlow[] = [];
    const runtimeSignals: RuntimeSignal[] = [];

    for (const scheduled of due) {
      if (scheduled.kind === "edge") {
        flows.push({
          edge: scheduled.edge,
          context: { ...scheduled.context, nowMs },
        });
      } else if (scheduled.kind === "signal") {
        runtimeSignals.push({
          ...scheduled.signal,
          context: { ...scheduled.context, nowMs },
        });
      }
    }

    return { flows, runtimeSignals, scheduledNodes: remainingScheduledNodes };
  }

  runStep(flow: BehaviorFlow): NodeExecutionResult {
    return flow.edge.node.execute({
      ...flow.context,
      triggeredInputFlowPort: flow.edge.port,
    });
  }

  drain(
    data: InjectedData,
    previousData: InjectedData | undefined,
    scheduledNodes: ScheduledNode[],
    previousPatch: PatchResult,
    nowMs = Date.now(),
    initialFlows?: BehaviorFlow[],
    currentNodeContext?: CurrentNodeContext,
    sceneAnimationsByName?: Record<
      string,
      import("./types.js").BehaviorAnimationPayload
    >,
    controlRoomSignals?: string[],
    customEventRuntimeEntries?: ReadonlyMap<string, CustomEventRuntimeEntry>,
    functionRuntimeEntries?: ReadonlyMap<string, FunctionRuntimeEntry>,
    variableRuntimeEntries?: ReadonlyMap<string, VariableRuntimeEntry>,
    variableOverrideApi?: VariableOverrideApi,
  ): RuntimeResult {
    const patches: PatchResult[] = [];

    const due =
      initialFlows && initialFlows.length > 0
        ? {
            flows: [...initialFlows],
            runtimeSignals: [] as RuntimeSignal[],
            scheduledNodes: scheduledNodes,
          }
        : this.seedFlowsFromDueScheduled(nowMs, scheduledNodes);

    const remainingScheduledNodes = due.scheduledNodes;

    const queue: BehaviorFlow[] = [
      ...due.flows,
      ...(initialFlows && initialFlows.length > 0
        ? []
        : this.seedFlowsFromEvents(
            nowMs,
            data,
            previousData,
            remainingScheduledNodes,
            currentNodeContext,
            sceneAnimationsByName,
            controlRoomSignals,
            customEventRuntimeEntries,
            functionRuntimeEntries,
            variableRuntimeEntries,
            variableOverrideApi,
          )),
    ];

    let eventOutputReached = false;
    let functionEndReached = false;

    // Traiter immédiatement les signaux dus
    for (const signal of due.runtimeSignals) {
      if (signal.type === "event_output_reached") eventOutputReached = true;
      if (signal.type === "function_end_reached") functionEndReached = true;
      queue.push(...this.handleRuntimeSignal(signal));
    }

    while (queue.length > 0) {
      const current = queue.shift()!;
      const result = this.runStep(current);

      patches.push(result.patch);

      if (result.scheduledNodes.length > 0) {
        remainingScheduledNodes.push(...result.scheduledNodes);
      }

      if (result.nextFlows.length > 0) {
        queue.push(...result.nextFlows);
      }

      if (result.runtimeSignals.length > 0) {
        for (const signal of result.runtimeSignals) {
          if (signal.type === "event_output_reached") eventOutputReached = true;
          if (signal.type === "function_end_reached") functionEndReached = true;
          queue.push(...this.handleRuntimeSignal(signal));
        }
      }
    }

    return {
      patches: mergePatches([previousPatch, ...patches]),
      nextWakeAt: this.getNextWakeAt(remainingScheduledNodes),
      nextFlows: queue,
      scheduledNodes: remainingScheduledNodes,
      eventOutputReached: eventOutputReached || undefined,
      functionEndReached: functionEndReached || undefined,
    };
  }
}
